/**
 * Save/Load module. Handles quick save (.pb.json), full save (.pb.zip with assets),
 * loading from both formats, and auto-save recovery detection.
 * JSZip is loaded dynamically from CDN only when ZIP operations are needed.
 */

import { normalizeEditableIdRepairState } from '../shared/editable-id-repair.js';
import { SITE_EFFECT_DEFAULTS, store } from './state.js';
import {
  stampAllSections,
  stampEditableIds,
  updateElementAttrsInHTML
} from '../editing/editable-utils.js';
import { RESPONSIVE_VIEWPORTS } from '../editing/section-utils.js';
import { normalizeLogoSlots } from '../editing/logo-normalizer.js';
import { normalizeMotionResidue } from '../shared/motion-residue.js';
import { getFeatureDefaults } from '../export/feature-registry.js';
import { normalizeClientInfo } from '../shared/client-info.js';
import { normalizeThemeTokens } from '../shared/theme-tokens.js';
import {
  canonicalizeAssetBackedMediaHTML,
  canonicalizeAssetBackedMediaInState
} from '../shared/asset-html.js';
import { extractButtonStyles, detectTemplateButtonEffect } from '../shared/button-style-extract.js';
// CE-5: page hydration is a REGISTERED seam, not a static import.
// section-manager self-registers at module load (the builder shell always
// loads it), so builder behavior is unchanged — but the restricted guest
// surfaces (review, client editor) can import readProjectBundle without
// bundling the structure-op modules, which their static-graph gates forbid.
// The fallback FAILS LOUD on a genuinely unhydrated page: silently saving
// past missing sections would be the parser-wipe failure shape.
let _pageHydration = null;
export function registerPageHydration(impl) {
  _pageHydration = impl || null;
}
let _thumbnailLoader = null;
export function registerThumbnailLoader(loader) {
  _thumbnailLoader = typeof loader === 'function' ? loader : null;
}
async function ensureAllPagesHydrated(options) {
  if (_pageHydration?.ensureAllPagesHydrated) {
    return _pageHydration.ensureAllPagesHydrated(options);
  }
  const pending = (store.getState()?.pages || []).filter((p) => p._pendingTemplateRefs?.length);
  if (pending.length) {
    throw new Error(
      `No page hydrator is registered and ${pending.length} page(s) are unhydrated — refusing to save a hole.`
    );
  }
}
function stopBackgroundPrefetch(options) {
  _pageHydration?.stopBackgroundPrefetch?.(options);
}
import { stampFreeformBlockRoots } from '../sections/freeform-section.js';
import { versionContentHashOf } from '../shared/version-hash.js';
import { reportError } from './report-error.js';
import { writeLastOpenedProject } from './last-opened-project.js';
import { LIFECYCLE, LIFECYCLE_OPERATION, classifyProjectSignal } from './project-lifecycle.js';
import { SECTION_SOURCE_HASH_RE } from '../shared/section-source-hash.js';
import { isValidIdProvenance } from '../shared/id-provenance.js';
import { FRAGMENT_CONTRACT_VERSION } from '../shared/mobile-menu-contract.js';
import { normalizeEntranceState, stripDefaultEntranceState } from '../shared/entrance-presets.js';
import { normalizeScheduleState } from '../shared/schedule-state.js';
import { normalizeBrandKitState } from '../shared/brand-kit-plan.js';
import { LOGO_WRAPPER_SELECTOR } from '../editing/logo-dom.js';
import { recordSessionBoundaryMetric } from './session-boundary-observability.js';

const JSZIP_CDN = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';

export class LoadSupersededError extends Error {
  constructor(reason) {
    const messages = {
      'read-only': 'Project load was cancelled because this tab is read-only.',
      'target-locked': 'Project load was cancelled because that project is open in another tab.',
      'project-changed': 'Project load was cancelled because another project is now open.',
      'epoch-changed': 'Project load was cancelled because the current project changed.'
    };
    super(messages[reason] || 'Project load was cancelled because the current project changed.');
    this.name = 'LoadSupersededError';
    this.reason = reason || 'epoch-changed';
  }
}

export function isLoadSupersededError(err) {
  return err instanceof LoadSupersededError || err?.name === 'LoadSupersededError';
}

// Installed by the shell to acquire the destination project's tab lock before
// a loaded state can alter either the asset cache or the autosave slot.
let prepareLoadedProject = async () => true;

export function configureLoadedProjectGuard(prepare) {
  prepareLoadedProject = typeof prepare === 'function' ? prepare : async () => true;
}

async function assertDestinationProjectClaimed(state) {
  if (!(await prepareLoadedProject(state))) throw new LoadSupersededError('target-locked');
}

function getLoadSupersededReason({ expectedEpoch, expectedProjectId, requireWritable } = {}) {
  if (requireWritable && !store.isWritable()) return 'read-only';
  const current = store.getState();
  if (expectedProjectId != null && current?.projectId !== expectedProjectId)
    return 'project-changed';
  if (expectedEpoch != null && store.getEpoch() !== expectedEpoch) return 'epoch-changed';
  return null;
}

function assertLoadStillCurrent(opts) {
  const reason = getLoadSupersededReason(opts);
  if (reason) throw new LoadSupersededError(reason);
}

function pendingNewerResult(projectId, version) {
  return { restored: false, pendingNewer: { projectId, version } };
}

const SEO_DEFAULTS = { title: '', description: '', ogImage: '', canonicalUrl: '' };
const BRAND_VOICE_DEFAULTS = {
  formality: null,
  contractions: null,
  sentenceLengthTarget: null,
  forbiddenWords: []
};
const FLAG_DEFAULTS = {
  aiRewrite: false,
  aiLogPrompts: true,
  aiQualityMode: false,
  renderDebug: false,
  doubleBuffer: false,
  srcdocMode: false,
  offThreadAssets: false
};
const LEGACY_LOGO_OVERRIDE_PROPS = ['width', 'maxWidth', 'marginLeft', 'marginRight'];

function coerceBooleanFlag(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
    return fallback;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on')
      return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off')
      return false;
  }
  return fallback;
}

function normalizeLegacyClassName(raw) {
  return String(raw || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * One parse, both DOM normalizers — the load-path twin of the store's write
 * path (state.js). Projects saved before the parser stripped frozen animation
 * state (Motion Restoration M3) carry it in html AND originalHtml; this is
 * where a JSON/ZIP import gets both cleaned before the store ever sees them.
 */
function normalizeLogoHTML(html, motionContext = null) {
  if (typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;
  const logoChanged = normalizeLogoSlots(root);
  const motionChanged = normalizeMotionResidue(root, {
    site: motionContext?.site || null,
    sectionId: motionContext?.sectionId || null
  }).changed;
  const normalized = logoChanged || motionChanged ? root.innerHTML : html;
  return canonicalizeAssetBackedMediaHTML(normalized);
}

function normalizeSectionLogoHTML(section) {
  if (!section || typeof section !== 'object') return;
  const context = {
    site: section.templateSiteName || null,
    sectionId: section.templateSectionId || null
  };
  if (typeof section.html === 'string') section.html = normalizeLogoHTML(section.html, context);
  if (typeof section.originalHtml === 'string')
    section.originalHtml = normalizeLogoHTML(section.originalHtml, context);
}

function legacyLogoWidthToSlotWidth(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? formatRemValue(value / 16) : '';
  }

  const raw = String(value || '').trim();
  const match = raw.match(/^([0-9]*\.?[0-9]+)\s*(px|rem)?$/i);
  if (!match) return '';

  const numeric = Number.parseFloat(match[1]);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';

  const unit = (match[2] || 'px').toLowerCase();
  return unit === 'rem' ? formatRemValue(numeric) : formatRemValue(numeric / 16);
}

function formatRemValue(value) {
  if (!Number.isFinite(value)) return '';
  const trimmed = Number(value.toFixed(4)).toString();
  return `${trimmed}rem`;
}

function migrateLegacyLogoOverrides(sectionHtml, elements) {
  if (
    !sectionHtml ||
    !elements ||
    typeof elements !== 'object' ||
    typeof DOMParser === 'undefined'
  ) {
    return { html: sectionHtml, htmlChanged: false, elementsChanged: false };
  }

  const doc = new DOMParser().parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return { html: sectionHtml, htmlChanged: false, elementsChanged: false };

  let htmlChanged = normalizeLogoSlots(root);
  let elementsChanged = false;

  for (const el of root.querySelectorAll('[data-pb-eid]')) {
    const eid = el.getAttribute('data-pb-eid');
    const props = eid ? elements[eid] : null;
    if (!props || typeof props !== 'object') continue;

    const logoWrapper = el.closest(LOGO_WRAPPER_SELECTOR);
    if (!logoWrapper) continue;

    const slot = logoWrapper.querySelector('.logo-slot');
    const slotWidth = legacyLogoWidthToSlotWidth(props.width ?? props.maxWidth);
    if (slot && slotWidth) {
      slot.style.setProperty('--pb-logo-slot-width', slotWidth);
      htmlChanged = true;
    }

    const nextProps = { ...props };
    let stripped = false;
    for (const prop of LEGACY_LOGO_OVERRIDE_PROPS) {
      if (Object.prototype.hasOwnProperty.call(nextProps, prop)) {
        delete nextProps[prop];
        stripped = true;
      }
    }

    if (!stripped) continue;
    elementsChanged = true;
    if (Object.keys(nextProps).length === 0) {
      delete elements[eid];
    } else {
      elements[eid] = nextProps;
    }
  }

  if (normalizeLogoSlots(root)) {
    htmlChanged = true;
  }

  return {
    html: htmlChanged ? root.innerHTML : sectionHtml,
    htmlChanged,
    elementsChanged
  };
}

function normalizeFontSizeOverride(raw) {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return { anchor: null, anchorPx: null, ratio: null, legacyValue: trimmed };
  }
  if (!raw || typeof raw !== 'object') return null;

  if (Number.isFinite(raw.anchorPx) && Number.isFinite(raw.ratio)) {
    return {
      anchor: typeof raw.anchor === 'string' ? raw.anchor : '',
      anchorPx: Number(raw.anchorPx),
      ratio: Number(raw.ratio)
    };
  }

  if (typeof raw.legacyValue === 'string') {
    const trimmed = raw.legacyValue.trim();
    if (!trimmed) return null;
    return { anchor: null, anchorPx: null, ratio: null, legacyValue: trimmed };
  }

  if (typeof raw.value === 'string') {
    const trimmed = raw.value.trim();
    if (!trimmed) return null;
    return { anchor: null, anchorPx: null, ratio: null, legacyValue: trimmed };
  }

  return null;
}

// Per-viewport override buckets (overrides.responsive.{desktop,tablet,mobile})
// were added after the base-override migration, so element overrides stored
// inside a bucket never got the same fontSize normalization + empty-entry
// cleanup — silently stranding legacy-shaped data. Mirror the base per-eid pass
// for each bucket. Legacy id/class HTML rewrites stay base-only (they mutate the
// shared section HTML and predate buckets), so buckets only normalize/prune.
function migrateResponsiveElementOverrides(section) {
  const responsive = section?.overrides?.responsive;
  if (!responsive || typeof responsive !== 'object') return;

  for (const viewport of RESPONSIVE_VIEWPORTS) {
    const bucket = responsive[viewport];
    if (!bucket || typeof bucket !== 'object') continue;
    if (!bucket.elements || typeof bucket.elements !== 'object') continue;

    const nextElements = { ...bucket.elements };
    let changed = false;
    for (const [eid, props] of Object.entries(nextElements)) {
      if (!props || typeof props !== 'object') {
        delete nextElements[eid];
        changed = true;
        continue;
      }
      const nextProps = { ...props };
      if (nextProps.fontSize !== undefined) {
        const normalized = normalizeFontSizeOverride(nextProps.fontSize);
        if (normalized) nextProps.fontSize = normalized;
        else delete nextProps.fontSize;
        changed = true;
      }
      // elementId/className were only ever written to base overrides; drop them
      // defensively if a bucket somehow carries them (they would strand).
      if (Object.prototype.hasOwnProperty.call(nextProps, 'elementId')) {
        delete nextProps.elementId;
        changed = true;
      }
      if (Object.prototype.hasOwnProperty.call(nextProps, 'className')) {
        delete nextProps.className;
        changed = true;
      }
      if (Object.keys(nextProps).length === 0) {
        delete nextElements[eid];
        changed = true;
      } else {
        nextElements[eid] = nextProps;
      }
    }

    if (!changed) continue;
    if (Object.keys(nextElements).length > 0) {
      bucket.elements = nextElements;
    } else {
      delete bucket.elements;
      if (Object.keys(bucket).length === 0) delete responsive[viewport];
    }
  }

  if (Object.keys(responsive).length === 0) delete section.overrides.responsive;
  if (section.overrides && Object.keys(section.overrides).length === 0) delete section.overrides;
}

function migrateSectionElementOverrides(section, contextLabel) {
  if (!section || typeof section !== 'object') return;
  normalizeSectionLogoHTML(section);
  if (!section.overrides || typeof section.overrides !== 'object') return;
  // Run the responsive-bucket pass first — a section can carry per-viewport
  // element overrides with NO base overrides.elements, so this must not sit
  // behind the base-elements early return below.
  migrateResponsiveElementOverrides(section);
  if (
    !section.overrides ||
    !section.overrides.elements ||
    typeof section.overrides.elements !== 'object'
  )
    return;

  const nextElements = { ...section.overrides.elements };
  let sectionHtml = section.html || '';
  let htmlChanged = false;
  let elementsChanged = false;

  if (
    sectionHtml &&
    !sectionHtml.includes('data-pb-eid=') &&
    !Object.hasOwn(section, 'editableIdRepairs')
  ) {
    const stamped = stampEditableIds(sectionHtml);
    sectionHtml = stamped.html;
    htmlChanged = htmlChanged || !!stamped.stamped;
  }

  const logoMigration = migrateLegacyLogoOverrides(sectionHtml, nextElements);
  sectionHtml = logoMigration.html;
  htmlChanged = htmlChanged || logoMigration.htmlChanged;
  elementsChanged = elementsChanged || logoMigration.elementsChanged;

  for (const [eid, props] of Object.entries(nextElements)) {
    if (!props || typeof props !== 'object') {
      delete nextElements[eid];
      elementsChanged = true;
      continue;
    }
    const nextProps = { ...props };

    if (nextProps.fontSize !== undefined) {
      const normalizedFontSize = normalizeFontSizeOverride(nextProps.fontSize);
      if (normalizedFontSize) {
        nextProps.fontSize = normalizedFontSize;
      } else {
        delete nextProps.fontSize;
      }
      elementsChanged = true;
    }

    const hasLegacyId = Object.prototype.hasOwnProperty.call(nextProps, 'elementId');
    const hasLegacyClass = Object.prototype.hasOwnProperty.call(nextProps, 'className');
    if (hasLegacyId || hasLegacyClass) {
      const updateResult = updateElementAttrsInHTML(sectionHtml, eid, {
        id: hasLegacyId ? String(nextProps.elementId || '').trim() : undefined,
        classNames: hasLegacyClass ? normalizeLegacyClassName(nextProps.className) : undefined
      });
      if (!updateResult.found) {
        console.warn('[SaveLoad] migrateState could not find editable for legacy override', {
          context: contextLabel,
          eid
        });
      }
      if (updateResult.changed) {
        sectionHtml = updateResult.html;
        htmlChanged = true;
      }
      delete nextProps.elementId;
      delete nextProps.className;
      elementsChanged = true;
    }

    if (Object.keys(nextProps).length === 0) {
      delete nextElements[eid];
      elementsChanged = true;
    } else {
      nextElements[eid] = nextProps;
    }
  }

  if (htmlChanged) section.html = sectionHtml;
  if (elementsChanged) {
    if (Object.keys(nextElements).length > 0) {
      section.overrides.elements = nextElements;
    } else {
      delete section.overrides.elements;
      if (Object.keys(section.overrides).length === 0) {
        delete section.overrides;
      }
    }
  }
}

function normalizeSectionCustomCSS(section) {
  if (!section || typeof section !== 'object') return;
  if (!section.overrides || typeof section.overrides !== 'object') return;
  if (!Object.prototype.hasOwnProperty.call(section.overrides, 'customCSS')) return;

  const value = section.overrides.customCSS;
  if (value == null) {
    delete section.overrides.customCSS;
  } else if (typeof value !== 'string') {
    section.overrides.customCSS = String(value);
  }

  if (Object.keys(section.overrides).length === 0) {
    delete section.overrides;
  }
}

function normalizeDecorativeSvgSection(section) {
  if (!section || typeof section !== 'object') return;

  if (typeof section.css === 'string' && section.css.includes('> :not(.pb-decorative-svg)')) {
    section.css = section.css
      .replace(
        /\n*\s*\[data-pb-section="[^"]+"\]\s*>\s*:not\(\.pb-decorative-svg\)\s*\{\s*position:\s*relative;\s*z-index:\s*1;\s*\}/g,
        ''
      )
      .replace(/\n{3,}/g, '\n\n');
  }

  if (
    typeof section.html !== 'string' ||
    !section.html.includes('pb-decorative-svg') ||
    typeof DOMParser === 'undefined'
  ) {
    return;
  }

  const doc = new DOMParser().parseFromString(`<div>${section.html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return;

  let changed = false;
  for (const el of root.querySelectorAll('[data-pb-decorative="true"].pb-decorative-svg')) {
    const classes = String(el.getAttribute('class') || '')
      .trim()
      .split(/\s+/)
      .filter((className) => className && className !== 'cs-graphic');
    const nextClass = Array.from(new Set(classes)).join(' ');
    if (nextClass !== el.getAttribute('class')) {
      if (nextClass) el.setAttribute('class', nextClass);
      else el.removeAttribute('class');
      changed = true;
    }
  }

  if (changed) section.html = root.innerHTML;
}

function migrateSpecialLightboxRoot(section) {
  if (!section || typeof section !== 'object' || typeof section.js !== 'string') return;
  const isSpecial9001 =
    section.catalogKey === 'stitch-9001' || section.sectionId === 'special-9001';
  const isSpecial9002 =
    section.catalogKey === 'stitch-9002' || section.sectionId === 'special-9002';
  if (!isSpecial9001 && !isSpecial9002) return;
  if (section.js.includes('sectionRoot.appendChild(lb)')) return;

  const id = isSpecial9001 ? '9001' : '9002';
  const needle = `var root = document.querySelector('#special-${id}');\n    if (!root) return;\n\n    var reel = root.querySelector('.cs-reel');\n    var gridDiv = root.querySelector('.cs-grid');`;
  if (!section.js.includes(needle) || !section.js.includes('root.appendChild(lb);')) return;

  const replacement = `var root = document.querySelector('#special-${id}');\n    if (!root) return;\n\n    var sectionRoot = root.matches && root.matches('[data-pb-section="special-${id}"]')\n        ? root\n        : root.querySelector('[data-pb-section="special-${id}"]') || root;\n\n    var reel = sectionRoot.querySelector('.cs-reel');\n    var gridDiv = sectionRoot.querySelector('.cs-grid');`;
  section.js = section.js
    .replace(needle, replacement)
    .replace('root.appendChild(lb);', 'sectionRoot.appendChild(lb);');
}

function migrateSpecialLampRoot(section) {
  if (!section || typeof section !== 'object' || typeof section.js !== 'string') return;
  const isSpecial9011 =
    section.catalogKey === 'stitch-9011' || section.sectionId === 'special-9011';
  if (!isSpecial9011 || section.js.includes('[data-pb-section="special-9011"]')) return;

  const needle =
    "  var hero = document.querySelector('#special-9011');\n  if (!hero || hero.dataset.lhInit === '1') return;\n  hero.dataset.lhInit = '1';";
  if (!section.js.includes(needle)) return;

  const replacement =
    "  var root = document.querySelector('#special-9011');\n  if (!root) return;\n  var hero = root.matches && root.matches('[data-pb-section=\"special-9011\"]')\n    ? root\n    : root.querySelector('[data-pb-section=\"special-9011\"]') || root;\n  if (!hero || hero.dataset.lhInit === '1') return;\n  hero.dataset.lhInit = '1';";
  section.js = section.js.replace(needle, replacement);
}

function migrateSpecialContainerScrollRoot(section) {
  if (!section || typeof section !== 'object' || typeof section.js !== 'string') return;
  const isSpecial9012 =
    section.catalogKey === 'stitch-9012' || section.sectionId === 'special-9012';
  if (!isSpecial9012 || section.js.includes('[data-pb-section="special-9012"]')) return;

  const needle =
    "  var hero = document.querySelector('#special-9012');\n  if (!hero || hero.dataset.csrInit === '1') return;\n  hero.dataset.csrInit = '1';";
  if (!section.js.includes(needle)) return;

  const replacement =
    "  var root = document.querySelector('#special-9012');\n  if (!root) return;\n  var hero = root.matches && root.matches('[data-pb-section=\"special-9012\"]')\n    ? root\n    : root.querySelector('[data-pb-section=\"special-9012\"]') || root;\n  if (!hero || hero.dataset.csrInit === '1') return;\n  hero.dataset.csrInit = '1';";
  section.js = section.js.replace(needle, replacement);
}

const BUTTON_ROOT_FIELDS = ['borderRadius', 'padding', 'backgroundColor', 'textColor'];

function collectAllInstances(state) {
  const out = [];
  const shared = state.sharedSections || {};
  if (shared.navigation) out.push(shared.navigation);
  for (const page of state.pages || []) {
    for (const section of page.sections || []) out.push(section);
  }
  if (shared.footer) out.push(shared.footer);
  return out;
}

/**
 * Drop malformed accept records. An accept must carry a string `key` — that is
 * what the disposition stage matches on and what the lock records; anything
 * else is unusable and is removed rather than carried forward.
 */
function normalizeAuditAccepts(state) {
  const raw = state.auditAccepts;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    state.auditAccepts = {};
    return;
  }
  const clean = {};
  for (const [digest, record] of Object.entries(raw)) {
    if (Object.keys(clean).length >= 5000) break;
    if (!/^(?:[0-9a-f]{16}|[0-9a-f]{64})$/i.test(String(digest))) continue;
    if (!record || typeof record !== 'object' || Array.isArray(record)) continue;
    if (typeof record.key !== 'string' || !record.key || record.key.length > 2048) continue;
    clean[String(digest)] = {
      key: record.key,
      by: typeof record.by === 'string' ? record.by.slice(0, 200) : null,
      note: typeof record.note === 'string' ? record.note.slice(0, 280) : null,
      at: Number.isFinite(record.at) ? record.at : null
    };
  }
  state.auditAccepts = clean;
}

/**
 * schemaVersion-5 provenance migration. Saved projects can't label whether their
 * buttonStyles were silently auto-extracted or explicitly user-edited, so we
 * reconstruct: recompute what the old first-section extractor WOULD have produced
 * and compare its root fields to the stored styles.
 *  - Match  → auto-extracted → RESET to detectTemplateButtonEffect (template styling).
 *  - Mismatch → user-edited → KEEP, tag origin:'user'.
 *  - No root fields (hoverEffect-only/empty) → tag origin:'auto'.
 * Idempotent: an already-labeled origin short-circuits.
 */
function migrateButtonProvenance(state) {
  const bs = state.buttonStyles;
  if (!bs || typeof bs !== 'object') return;
  if (bs.origin === 'user' || bs.origin === 'auto') return; // already labeled
  const hasRootRules = BUTTON_ROOT_FIELDS.some((f) => bs[f] != null);
  if (!hasRootRules) {
    bs.origin = 'auto';
    return;
  }
  const sections = collectAllInstances(state);
  const reconstructed = extractButtonStyles(sections) || {};
  const matchesAutoExtraction = BUTTON_ROOT_FIELDS.every(
    (f) => (bs[f] || '') === (reconstructed[f] || '')
  );
  if (matchesAutoExtraction) {
    state.buttonStyles = detectTemplateButtonEffect(sections);
  } else {
    bs.origin = 'user';
  }
}

/**
 * schemaVersion-6 migration: normalize section provenancePath
 * (docs/fleet-contracts.md §1). The loader stamps it on every instance created
 * from v6 on; here we only guarantee the field is either a non-empty string or
 * absent. No reverse resolution — duplicate (templateSiteName, catalogKey)
 * pairs with different content make that unsafe, and the ledger is not
 * available synchronously. Idempotent.
 */
function migrateSectionProvenance(state) {
  for (const section of collectAllInstances(state)) {
    if (!section || typeof section !== 'object') continue;
    if ('provenancePath' in section) {
      if (typeof section.provenancePath !== 'string' || !section.provenancePath.trim()) {
        delete section.provenancePath;
      }
    }
    if ('sourceHash' in section && !SECTION_SOURCE_HASH_RE.test(section.sourceHash || '')) {
      delete section.sourceHash;
    }
    // Workstream A: idProvenance is either a valid ownership record or absent.
    // Absent ⇒ the export id plan treats the instance conservatively (no
    // descendant renames); never fabricate one from current HTML — that is
    // the originalHtml mistake this field exists to correct.
    if ('idProvenance' in section && !isValidIdProvenance(section.idProvenance)) {
      delete section.idProvenance;
    }
  }
}

/**
 * v7 invariant (UX Pack F4a): every direct freeform block root carries a
 * persisted editable identity so selection/geometry/override writes target the
 * BLOCK, never its first eid-carrying descendant. Idempotent; DOMParser-less
 * environments are a clean no-op (stampFreeformBlockRoots guards).
 */
function migrateFreeformBlockRoots(state) {
  for (const section of collectAllInstances(state)) {
    if (!section || section.type !== 'freeform' || typeof section.html !== 'string') continue;
    if (Object.hasOwn(section, 'editableIdRepairs')) continue;
    const { html, changed } = stampFreeformBlockRoots(section.html);
    if (changed) section.html = html;
  }
}

let _sectionProvenanceLedgerPromise = null;

/** Persist only unambiguous legacy identities. A path can be recovered from a
 * unique (templateSiteName, catalogKey) pair; the historical source bytes
 * cannot, so sourceHash deliberately remains absent/unknown. */
export async function resolveLegacySectionProvenance(state, { fetchImpl = globalThis.fetch } = {}) {
  const unresolved = collectAllInstances(state).filter(
    (section) =>
      section?.catalogKey &&
      !(typeof section.provenancePath === 'string' && section.provenancePath.trim())
  );
  if (unresolved.length === 0 || typeof fetchImpl !== 'function') return state;

  if (!_sectionProvenanceLedgerPromise) {
    _sectionProvenanceLedgerPromise = fetchImpl('./data/section-provenance.json')
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  const ledger = await _sectionProvenanceLedgerPromise;
  const entries = ledger?.entries && typeof ledger.entries === 'object' ? ledger.entries : null;
  if (!entries) return state;

  const index = new Map();
  for (const [path, entry] of Object.entries(entries)) {
    if (!entry?.catalogKey) continue;
    const key = `${entry.templateSiteName || ''}::${entry.catalogKey}`;
    if (!index.has(key)) index.set(key, []);
    index.get(key).push(path);
  }
  for (const section of unresolved) {
    const matches = index.get(`${section.templateSiteName || ''}::${section.catalogKey}`) || [];
    if (matches.length === 1) section.provenancePath = matches[0];
  }
  return state;
}

/**
 * Migrate state from any previous version to current shape.
 * Hardens against malformed/partial states. Must be called BEFORE validateState().
 */
export function migrateState(state) {
  if (!state || typeof state !== 'object') return state;

  // Phase 1: ownership + schema versioning (additive, all defaults).
  // `admin` is reserved as the system namespace; Phase 6 must not assign it to real users.
  if (typeof state.projectId !== 'string' || !state.projectId)
    state.projectId = crypto.randomUUID();
  if (typeof state.ownerSub !== 'string' || !state.ownerSub) state.ownerSub = 'admin';
  if (typeof state.createdAt !== 'number') state.createdAt = Date.now();
  if (typeof state.updatedAt !== 'number') state.updatedAt = Date.now();
  if (typeof state.schemaVersion !== 'number') state.schemaVersion = 1;
  // cloudVersion: the server latest_version this project last synced from (null =
  // never synced). Additive, no schemaVersion bump; direct-mutated like updatedAt,
  // so it never enters undo history and never travels in a portable bundle.
  if (typeof state.cloudVersion !== 'number') state.cloudVersion = null;

  // templateCSS is no longer persisted; it is live-fetched at render time.
  // Unconditional delete repairs older projects whose IndexedDB still carries
  // stale template CSS (e.g. cornerstone's body{overflow:hidden} leakage).
  if ('templateCSS' in state) {
    delete state.templateCSS;
  }
  if (state.schemaVersion < 2) {
    state.schemaVersion = 2;
  }
  if (state.schemaVersion < 3) {
    // v3 adds state.sourceClientProfile — captured at template load so that
    // Resync can rename old template values (Blade Works Studio → user's name).
    // Legacy projects have no snapshot; resync falls back to bind-only.
    if (!('sourceClientProfile' in state)) state.sourceClientProfile = null;
    state.schemaVersion = 3;
  }
  if (state.schemaVersion < 4) {
    // v4 adds state.compositionContext — written by Skin from URL so sections
    // added after composing get re-skinned on insert. null = dormant.
    if (!('compositionContext' in state)) state.compositionContext = null;
    state.schemaVersion = 4;
  }
  if (state.schemaVersion < 5) {
    // v5: button-style provenance. Silent auto-extraction (first section's
    // colors becoming the whole-site button style) is gone; saved projects can't
    // label provenance, so reconstruct it and RESET silently-extracted styles
    // while KEEPING explicit user edits. See migrateButtonProvenance.
    migrateButtonProvenance(state);
    state.schemaVersion = 5;
  }
  if (state.schemaVersion < 6) {
    // v6: section provenance (docs/fleet-contracts.md §1). Instances created
    // after this version carry provenancePath, stamped by the loader that
    // fetched the source JSON. This migration only normalizes the field —
    // non-string/empty values become absent. Legacy instances are NOT resolved
    // here: reverse lookup is unsafe (duplicate (templateSiteName, catalogKey)
    // pairs with different content) and migrateState is synchronous while the
    // provenance ledger is a fetch. The lock builder performs the contract's
    // unique-match-only legacy fallback at read time and never persists a guess.
    state.schemaVersion = 6;
  }
  // Normalization is an invariant, not just a one-time ladder step: malformed
  // v6 input must not smuggle a non-hash value into a future lock.
  migrateSectionProvenance(state);

  if (state.schemaVersion < 7) {
    // v7 (UX Pack F4a): every direct freeform block root carries a persisted
    // editable identity — see stampFreeformBlockRoots. The ladder step is a
    // marker; the invariant below runs on every load like provenance.
    state.schemaVersion = 7;
  }
  if (state.schemaVersion < 8) {
    // v8 (Export Readiness P3): state.auditAccepts. The ladder step is a
    // marker; normalization below runs on EVERY load like provenance, because a
    // malformed record from an older client or a collab peer must not smuggle a
    // non-string key into the export lock.
    state.schemaVersion = 8;
  }
  normalizeAuditAccepts(state);

  // Entrance animations (EA-1). NO schemaVersion bump: a missing `entrance`
  // means exactly the defaults, so re-materializing it here is what keeps every
  // legacy project's content hash byte-identical (preparePortableState omits it
  // again on the way out). Invariant, not a ladder step — a malformed shape from
  // an older client or a collab peer is repaired on every load.
  normalizeEntranceState(state);
  // SC-1: the schedule invariant runs on EVERY load path like the two above —
  // a malformed schedule from an older client or a collab peer must not
  // smuggle a non-shape into the lock's scheduleRules or the sidecar.
  normalizeScheduleState(state);
  normalizeEditableIdRepairState(state);

  // LogoNuri brand-kit provenance (LN-4a). Same reasoning as `entrance` above:
  // NO schemaVersion bump, because ABSENT means "no kit was ever applied",
  // which is a valid and extremely common state rather than a missing field.
  // Invariant rather than a ladder step — a malformed record from an older
  // client or a collab peer is repaired on every load, and a record whose
  // assets are not `pb-asset://` is dropped rather than trusted (a remote URL
  // there would mean the project is hotlinking a revocable third-party asset).
  const brandKit = normalizeBrandKitState(state.brandKit);
  if (brandKit) state.brandKit = brandKit;
  else delete state.brandKit;

  // Invariant, not one-time: a freeform block inserted by an older client (or
  // a collab peer on old code) must be repaired on the next load, and the
  // stamp is idempotent — run twice ≡ once.
  migrateFreeformBlockRoots(state);

  state.theme = normalizeThemeTokens(state.theme || {}, { preserveExisting: true });

  if (typeof state.projectName !== 'string' || !state.projectName.trim()) {
    state.projectName = 'Untitled';
  }

  state.clientInfo = normalizeClientInfo(state.clientInfo, state.projectName);

  // Guard: ensure pages is an array
  if (!Array.isArray(state.pages)) state.pages = [];

  // Step 1: Filter out non-object entries, then fix fields and collect IDs
  state.pages = state.pages.filter((p) => p != null && typeof p === 'object');

  const seenIds = new Set();
  for (const page of state.pages) {
    if (!page.id) page.id = crypto.randomUUID();
    if (!page.name) page.name = 'Untitled';
    if (!Array.isArray(page.sections)) page.sections = [];
    page.seo = { ...SEO_DEFAULTS, ...(page.seo || {}) };

    // Duplicate ID repair
    if (seenIds.has(page.id)) {
      page.id = crypto.randomUUID();
    }
    seenIds.add(page.id);
  }

  // Step 2: Ensure at least one page exists
  if (state.pages.length === 0) {
    state.pages.push({ id: 'page-1', name: 'Home', sections: [], seo: { ...SEO_DEFAULTS } });
  }

  // Step 3: Validate activePage ONCE after all IDs are finalized
  if (!state.pages.some((p) => p.id === state.activePage)) {
    state.activePage = state.pages[0].id;
  }

  // Shared sections: ensure object exists
  if (!state.sharedSections || typeof state.sharedSections !== 'object') {
    state.sharedSections = { navigation: null, footer: null };
  }

  // Section type: default to 'template' for sections that predate the freeform feature
  for (const page of state.pages) {
    for (const section of page.sections) {
      if (!section.type) section.type = 'template';
    }
  }
  if (state.sharedSections?.navigation && !state.sharedSections.navigation.type) {
    state.sharedSections.navigation.type = 'template';
  }
  if (state.sharedSections?.footer && !state.sharedSections.footer.type) {
    state.sharedSections.footer.type = 'template';
  }

  // Mobile-menu fragment state (MM-2): additive, no schemaVersion bump, and an
  // invariant rather than a ladder step — a malformed shape from an old client
  // or hand-edited file is dropped, never trusted. The merged drawer HTML/CSS
  // lives in the nav itself, so only revert/identity metadata is lost; without
  // a valid stockSnapshot a revert would be a lie.
  const navShared = state.sharedSections?.navigation;
  if (navShared && 'mobileMenu' in navShared) {
    const mm = navShared.mobileMenu;
    const snap = mm && typeof mm === 'object' ? mm.stockSnapshot : null;
    const valid =
      mm &&
      typeof mm === 'object' &&
      typeof mm.fragmentKey === 'string' &&
      mm.fragmentKey &&
      mm.fragmentContractVersion === FRAGMENT_CONTRACT_VERSION &&
      snap &&
      typeof snap === 'object' &&
      typeof snap.drawerHtml === 'string' &&
      typeof snap.drawerCss === 'string';
    if (!valid) delete navShared.mobileMenu;
  }

  // Migrate per-element override payloads and fold legacy id/class overrides
  // directly into section HTML.
  for (let pi = 0; pi < state.pages.length; pi += 1) {
    const page = state.pages[pi];
    for (let si = 0; si < (page.sections || []).length; si += 1) {
      normalizeSectionCustomCSS(page.sections[si]);
      normalizeDecorativeSvgSection(page.sections[si]);
      migrateSpecialLightboxRoot(page.sections[si]);
      migrateSpecialLampRoot(page.sections[si]);
      migrateSpecialContainerScrollRoot(page.sections[si]);
      migrateSectionElementOverrides(page.sections[si], `pages.${pi}.sections.${si}`);
    }
  }
  if (state.sharedSections?.navigation) {
    normalizeSectionCustomCSS(state.sharedSections.navigation);
    normalizeDecorativeSvgSection(state.sharedSections.navigation);
    migrateSpecialLampRoot(state.sharedSections.navigation);
    migrateSpecialContainerScrollRoot(state.sharedSections.navigation);
    migrateSectionElementOverrides(state.sharedSections.navigation, 'sharedSections.navigation');
  }
  if (state.sharedSections?.footer) {
    normalizeSectionCustomCSS(state.sharedSections.footer);
    normalizeDecorativeSvgSection(state.sharedSections.footer);
    migrateSpecialLampRoot(state.sharedSections.footer);
    migrateSpecialContainerScrollRoot(state.sharedSections.footer);
    migrateSectionElementOverrides(state.sharedSections.footer, 'sharedSections.footer');
  }

  // Code injection (forward-compat for Phase 12)
  if (state.headCode === undefined) state.headCode = '';
  if (state.bodyCode === undefined) state.bodyCode = '';

  // Button styles: sanitize hoverEffect. List must mirror the full set the
  // Buttons sub-panel exposes (property-panel.js _renderButtonsSubpanel
  // hover-effect grid). Otherwise saved values silently reset to 'none' on
  // reload — which is what was happening for wipe / ripple / glitch / tilt /
  // spotlight before this list was extended.
  if (state.buttonStyles && state.buttonStyles.hoverEffect) {
    const validEffects = [
      'none',
      'bubble',
      'shine',
      'rubber',
      'glow',
      'magnetic',
      'wipe',
      'ripple',
      'glitch',
      'tilt',
      'spotlight'
    ];
    if (!validEffects.includes(state.buttonStyles.hoverEffect)) {
      state.buttonStyles.hoverEffect = 'none';
    }
  }

  if (state.formStyles && typeof state.formStyles === 'object') {
    const shapes = new Set(['square', 'slight', 'medium', 'round', 'pill']);
    const styles = new Set(['filled', 'outline', 'underline', 'ghost']);
    const submits = new Set(['square', 'pill', 'round', 'outline']);
    const nextFormStyles = {};
    if (shapes.has(state.formStyles.shape)) nextFormStyles.shape = state.formStyles.shape;
    if (styles.has(state.formStyles.style)) nextFormStyles.style = state.formStyles.style;
    if (submits.has(state.formStyles.submit)) nextFormStyles.submit = state.formStyles.submit;
    state.formStyles = Object.keys(nextFormStyles).length ? nextFormStyles : null;
  } else {
    state.formStyles = null;
  }

  // Feature flags (Project Builder)
  if (!state.features || typeof state.features !== 'object') {
    state.features = getFeatureDefaults();
  }
  const featureDefaults = getFeatureDefaults();
  for (const [key, defaultVal] of Object.entries(featureDefaults)) {
    if (state.features[key] === undefined) state.features[key] = defaultVal;
  }

  // PWA app identity (icon + manifest screenshots). Entries missing dimensions
  // or a MIME type are dropped rather than repaired: the exporter cannot
  // measure a blob under Node, so an entry without them can only produce an
  // invalid manifest screenshot.
  if (!state.pwaInstall || typeof state.pwaInstall !== 'object') {
    state.pwaInstall = { appIcon: null, screenshots: [] };
  }

  // ES-5: contract templates seeded into an exported site. Filtered rather than
  // trusted — a malformed entry would be JSON.stringify'd straight into an
  // emitted file, so a project saved before this key existed, or hand-edited,
  // must not be able to produce an unparseable seed module.
  state.esignTemplates = Array.isArray(state.esignTemplates)
    ? state.esignTemplates
        .filter((t) => t && typeof t === 'object' && Array.isArray(t.sections))
        .map((t) => ({
          name: typeof t.name === 'string' ? t.name : '',
          templateKey: typeof t.templateKey === 'string' ? t.templateKey : '',
          description: typeof t.description === 'string' ? t.description : '',
          status: t.status === 'draft' ? 'draft' : 'active',
          keyLocked: t.keyLocked === true,
          sections: t.sections
            .filter((sec) => sec && typeof sec === 'object')
            .map((sec) => ({
              heading: typeof sec.heading === 'string' ? sec.heading : '',
              body: typeof sec.body === 'string' ? sec.body : ''
            }))
        }))
    : [];
  if (typeof state.pwaInstall.appIcon !== 'string' || !state.pwaInstall.appIcon) {
    state.pwaInstall.appIcon = null;
  }
  state.pwaInstall.screenshots = Array.isArray(state.pwaInstall.screenshots)
    ? state.pwaInstall.screenshots.filter(
        (s) =>
          s &&
          typeof s.asset === 'string' &&
          s.asset &&
          Number.isFinite(s.width) &&
          Number.isFinite(s.height) &&
          typeof s.type === 'string' &&
          s.type
      )
    : [];

  // Site effects (live-preview affordances, separate from export feature packs)
  if (!state.siteEffects || typeof state.siteEffects !== 'object') {
    state.siteEffects = { ...SITE_EFFECT_DEFAULTS };
  }
  for (const [key, defaultVal] of Object.entries(SITE_EFFECT_DEFAULTS)) {
    if (state.siteEffects[key] === undefined) state.siteEffects[key] = defaultVal;
  }

  // Text-edit AI flags (Phase 5)
  if (!state.flags || typeof state.flags !== 'object') state.flags = {};
  for (const [key, defaultVal] of Object.entries(FLAG_DEFAULTS)) {
    if (state.flags[key] === undefined) state.flags[key] = defaultVal;
  }
  state.flags.aiRewrite = coerceBooleanFlag(state.flags.aiRewrite, false);
  state.flags.aiLogPrompts = coerceBooleanFlag(state.flags.aiLogPrompts, true);
  state.flags.aiQualityMode = coerceBooleanFlag(state.flags.aiQualityMode, false);
  state.flags.renderDebug = coerceBooleanFlag(state.flags.renderDebug, false);
  state.flags.doubleBuffer = coerceBooleanFlag(state.flags.doubleBuffer, false);
  state.flags.srcdocMode = coerceBooleanFlag(state.flags.srcdocMode, false);
  state.flags.offThreadAssets = coerceBooleanFlag(state.flags.offThreadAssets, false);

  // Brand voice settings (Phase 5)
  if (!state.brandVoice || typeof state.brandVoice !== 'object')
    state.brandVoice = { ...BRAND_VOICE_DEFAULTS };
  if (state.brandVoice.formality === undefined)
    state.brandVoice.formality = BRAND_VOICE_DEFAULTS.formality;
  if (state.brandVoice.contractions === undefined)
    state.brandVoice.contractions = BRAND_VOICE_DEFAULTS.contractions;
  if (state.brandVoice.sentenceLengthTarget === undefined)
    state.brandVoice.sentenceLengthTarget = BRAND_VOICE_DEFAULTS.sentenceLengthTarget;
  if (!Array.isArray(state.brandVoice.forbiddenWords)) state.brandVoice.forbiddenWords = [];

  const parsedFormality = Number.parseInt(state.brandVoice.formality, 10);
  state.brandVoice.formality =
    Number.isInteger(parsedFormality) && parsedFormality >= 1 && parsedFormality <= 5
      ? parsedFormality
      : null;
  const contractions = String(state.brandVoice.contractions || '')
    .trim()
    .toLowerCase();
  state.brandVoice.contractions =
    contractions === 'allow' || contractions === 'avoid' ? contractions : null;
  const parsedSentenceLength = Number.parseInt(state.brandVoice.sentenceLengthTarget, 10);
  state.brandVoice.sentenceLengthTarget =
    Number.isInteger(parsedSentenceLength) && parsedSentenceLength > 0
      ? parsedSentenceLength
      : null;
  state.brandVoice.forbiddenWords = state.brandVoice.forbiddenWords
    .map((word) => (typeof word === 'string' ? word.trim() : ''))
    .filter(Boolean);

  // Deploy settings — drives 11ty exporter substitutions for admin/config.yml
  // (Decap GitHub repo, base_url, site_domain) and the OAuth allowlist. States
  // saved before this field default to empty strings, which fall back to
  // placeholder values in the export so the user still gets a [CHANGE]-style
  // config they can hand-edit.
  if (!state.deploy || typeof state.deploy !== 'object') {
    state.deploy = { githubRepo: '', siteDomain: '', cmsAllowedUsers: [], analyticsWebsiteId: '' };
  } else {
    if (typeof state.deploy.githubRepo !== 'string') state.deploy.githubRepo = '';
    if (typeof state.deploy.siteDomain !== 'string') state.deploy.siteDomain = '';
    if (!Array.isArray(state.deploy.cmsAllowedUsers)) state.deploy.cmsAllowedUsers = [];
    if (typeof state.deploy.analyticsWebsiteId !== 'string') state.deploy.analyticsWebsiteId = '';
  }

  // Source template tracking (added for 11ty export reference bundles).
  // States saved before this field existed default to null; the exporter
  // treats null as "no starting template — skip _reference/original-template/".
  if (state.sourceTemplateDomain === undefined) state.sourceTemplateDomain = null;

  return state;
}
let JSZip = null;

async function loadJSZip() {
  if (JSZip) return JSZip;
  // JSZip's UMD build assigns to window.JSZip
  if (window.JSZip) {
    JSZip = window.JSZip;
    return JSZip;
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = JSZIP_CDN;
    script.onload = () => {
      JSZip = window.JSZip;
      if (!JSZip) {
        reject(new Error('JSZip failed to load'));
        return;
      }
      resolve(JSZip);
    };
    script.onerror = () => reject(new Error('Failed to load JSZip from CDN'));
    document.head.appendChild(script);
  });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9_\- ]/g, '').trim() || 'Untitled';
}

/**
 * The portable form of a state — the EXACT object every save path serializes.
 *
 * cloudVersion is per-device cloud-sync bookkeeping, not portable project data.
 * Stripping it means an imported bundle starts un-synced (null → baseVersion 0),
 * and a cloud-restored bundle gets its base set explicitly by the restore path.
 * Media canonicalization rewrites render-time src/srcset (blob:/data:) back to
 * durable pb-asset:// form.
 *
 * Exported because `versionContentHashOf` (shared/version-hash.js) must hash
 * THIS object, never the live state: the live object still carries cloudVersion
 * and un-canonicalized media, so hashing it would let two different saved
 * bundles claim the same content — a false "no change" verdict.
 */
export function preparePortableState(state) {
  const saveState = structuredClone(state);
  delete saveState.cloudVersion;
  delete saveState.identityRepairLocalOrigin;
  canonicalizeAssetBackedMediaInState(saveState);
  stripDefaultEntranceState(saveState);
  // SC-1: the portable form represents "unscheduled" by ABSENCE — a live
  // `schedule: null` (the drawer's Clear writes one) must not move the
  // contentHash of a project that has no schedules.
  normalizeScheduleState(saveState);
  return saveState;
}

/**
 * D1b — content-hash identity. `entrance` is NOT in VERSION_HASH_IGNORED_PATHS
 * (it is real content: a preset changes what ships), so materializing the
 * defaults on load would move the contentHash of every project that has never
 * heard of the feature. The fix is symmetry, not an ignore entry: absent ≡
 * defaults, so the portable form OMITS the field when it holds the defaults and
 * the load-path normalizer puts it back. A project that actually set a preset or
 * turned the template convention off keeps the field and hashes differently,
 * which is correct — its export really did change.
 *
 * Sections use the same rule via absence: `normalizeEntranceState` deletes an
 * inherit-valued `revealPreset` rather than storing `''`, and this sweep repairs
 * a state that reached the save path without passing through it.
 */
/**
 * Quick save: state JSON only. Image refs remain as pb-asset:// IDs.
 */
export async function quickSave() {
  await ensureAllPagesHydrated();
  const state = store.getState();
  if (!state) throw new Error('No state to save');

  const saveState = preparePortableState(state);
  const json = JSON.stringify(saveState, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const filename = `${sanitizeFilename(state.projectName)}.pb.json`;
  triggerDownload(blob, filename);
  return filename;
}

/**
 * Collect all pb-asset:// IDs referenced in the current state.
 */
function collectReferencedAssetIds(state) {
  const ids = new Set();
  const json = JSON.stringify(state);
  const re = /pb-asset:\/\/([a-f0-9-]+)/g;
  let match;
  while ((match = re.exec(json)) !== null) {
    ids.add(match[1]);
  }
  return ids;
}

/**
 * Build a project ZIP: state JSON + only referenced assets.
 * Structure: state.json + assets/{id}.{ext}
 */
export async function buildProjectZipBlob(state = store.getState()) {
  if (!state) throw new Error('No state to save');
  if (state === store.getState() && state.pages?.some((p) => p._pendingTemplateRefs?.length)) {
    await ensureAllPagesHydrated();
    state = store.getState();
  }

  const saveState = preparePortableState(state);

  await loadJSZip();
  const zip = new JSZip();

  // Add state JSON
  zip.file('state.json', JSON.stringify(saveState, null, 2));

  // Only export assets actually referenced by state (not stale orphans).
  // Attribution metadata is captured in the SAME loop so it covers exactly the
  // assets written into the zip — never orphans from exportAll().
  const referencedIds = collectReferencedAssetIds(saveState);
  if (referencedIds.size > 0) {
    const assetsFolder = zip.folder('assets');
    const attribution = {};
    for (const id of referencedIds) {
      const record = await store.assets.get(id);
      if (!record) {
        console.warn(`[SaveLoad] Referenced asset ${id} not found in IndexedDB — skipping`);
        continue;
      }
      const ext = extensionFromMime(record.mime, record.filename);
      assetsFolder.file(`${id}.${ext}`, record.blob);
      if (record.source || record.author || record.authorUrl) {
        attribution[id] = {
          ...(record.source ? { source: record.source } : {}),
          ...(record.author ? { author: record.author } : {}),
          ...(record.authorUrl ? { authorUrl: record.authorUrl } : {})
        };
      }
    }
    if (Object.keys(attribution).length > 0) {
      zip.file('assets-meta.json', JSON.stringify(attribution, null, 2));
    }
  }

  return await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

/**
 * Full save: state JSON + only referenced assets in a ZIP.
 */
export async function fullSave() {
  await ensureAllPagesHydrated();
  const state = store.getState();
  if (!state) throw new Error('No state to save');

  const zipBlob = await buildProjectZipBlob(state);
  const filename = `${sanitizeFilename(state.projectName)}.pb.zip`;
  triggerDownload(zipBlob, filename);
  return filename;
}

/**
 * Phase 6 UI: cloud save — POST the project zip to the Worker, which creates
 * a new D1-backed version row and stores the bundle in R2. Requires the user
 * to be authenticated (the request sends `pb_token` cookie via credentials).
 *
 * Returns the saved version metadata, or throws on auth failure / network
 * error. Callers in shell.js show toasts based on success/failure.
 *
 * `syncedUpdatedAt` is the state.updatedAt this save actually uploaded, captured in
 * the same synchronous turn as the zip snapshot. Auto-sync bookkeeping must stamp
 * its clock from THIS value, never from a post-await `store.getState().updatedAt`:
 * an edit landing while the upload is in flight advances the live updatedAt past
 * what was uploaded, and stamping that would mark the un-uploaded edit as synced.
 *
 * `skipIfUnchanged` makes the save a no-op when this project's `updatedAt` has not
 * advanced past what a previous save/load already put on the server, returning
 * `{skipped:true}` without a zip build or a request. Opt-in: every other caller
 * keeps the unconditional behaviour.
 *
 * @param {{captureSnapshotContext?:(state:object)=>unknown, skipIfUnchanged?:boolean,
 *   expectedEpoch?:number, expectedProjectId?:string}} [options]
 * @returns {Promise<{ok:true, skipped?:boolean, project:object, version:{version:number,createdAt:number}|null, syncedUpdatedAt:number, snapshotContext?:unknown}>}
 */
// Module-scope single-flight chain. Concurrent cloudSave() triggers (the 30s
// auto-sync tick colliding with a manual click, a Retry, etc.) run strictly
// sequentially — each snapshots state AFTER the previous save settles, so two
// triggers can't mint two versions from duplicate/stale snapshots. A failed
// save does not break the chain for the next caller.
let _cloudSaveChain = Promise.resolve();

/**
 * Per-project `state.updatedAt` that a cloud save (or a cloud load) is known to
 * have put on the server. Only consulted by callers passing `skipIfUnchanged`.
 *
 * Absent entry means "unknown", and unknown always saves: after an IDB-autosave
 * recovery boot the local copy may legitimately be ahead of the cloud, and
 * fabricating a baseline there would drop unsynced work. That costs at most one
 * no-op version per page lifetime.
 */
const _cloudSyncedUpdatedAt = new Map();

/**
 * Has this project ever been confirmed to exist in the cloud for this client?
 *
 * `cloudVersion` IS that latch, already — it is the server `latest_version` we
 * last synced from, `null` means never synced, migrateState assigns it to every
 * project, it persists through the autosave slot, and preparePortableState
 * strips it so an imported bundle correctly reads as never-synced. Introducing a
 * separate flag would duplicate all of that and could drift from it.
 *
 * Why it matters: the server returns the same 404 for a deleted project and for
 * one that never existed. Without provenance, "404 ⇒ gone" would refuse to edit
 * every brand-new project, which 404s entirely correctly.
 */
export function everConfirmedInCloud(state) {
  return typeof state?.cloudVersion === 'number' && Number.isInteger(state.cloudVersion);
}

/**
 * Record that `updatedAt` for `projectId` is now represented on the server.
 *
 * FN-5 widened the value from a bare stamp to `{updatedAt, principal}`. The
 * principal is load-bearing for the durability contract, not bookkeeping: a
 * confirmation recorded under one account is not evidence about another, and
 * without it "confirmed for the current account" could only ever be assumed.
 */
export function markCloudSynced(projectId, updatedAt) {
  const stamp = Number(updatedAt);
  if (!projectId || !Number.isFinite(stamp) || stamp <= 0) return;
  _cloudSyncedUpdatedAt.set(projectId, {
    updatedAt: stamp,
    principal: store.getPrincipalScope?.() ?? null
  });
}

/**
 * Provenance of the last CONFIRMED cloud save for a project in this session, or
 * null. `evaluateDurability` reads this; nothing else should infer sync state.
 */
export function getCloudSyncRecord(projectId) {
  return _cloudSyncedUpdatedAt.get(projectId) || null;
}

/** Test seam — the Map is module state that would otherwise leak between cases. */
export function _resetCloudSyncedForTesting() {
  _cloudSyncedUpdatedAt.clear();
}

/**
 * cloudVersion is direct-mutated on the live state (like updatedAt — never in undo
 * history). This best-effort persists the change into the autosave slot so a
 * refresh keeps the correct optimistic-concurrency base. Failure is non-fatal (a
 * later mutation re-triggers autosave) and reported silently.
 */
export async function setCloudVersionAndPersist(projectId, version) {
  if (!Number.isInteger(version)) return;
  const live = store.getState();
  if (!live || live.projectId !== projectId) return;
  live.cloudVersion = version;
  delete live.identityRepairLocalOrigin;
  try {
    await store.saveToIDB();
  } catch (err) {
    reportError('SaveLoad cloudVersion slot', err, {
      user: 'silent',
      detail: 'autosave slot write after cloudVersion update failed'
    });
  }
}

export function cloudSave(options = {}) {
  const run = async () => {
    assertLoadStillCurrent({
      expectedEpoch: options.expectedEpoch,
      expectedProjectId: options.expectedProjectId
    });
    await ensureAllPagesHydrated();
    const state = store.getState();
    if (!state) throw new Error('No state to save');
    if (!state.projectId) throw new Error('Project has no projectId');
    assertLoadStillCurrent({
      expectedEpoch: options.expectedEpoch,
      expectedProjectId: options.expectedProjectId
    });

    // Opt-in no-op guard. The server's own hash dedupe can never fire — JSZip
    // stamps a fresh mtime per entry, so byte-identical content still hashes
    // differently — which is why an unconditional save mints a version every
    // time. `updatedAt` is the codebase's established dirty signal (store.js
    // bumps it on every mutation; the auto-sync tick gates on it).
    if (options.skipIfUnchanged) {
      const baseline = _cloudSyncedUpdatedAt.get(state.projectId)?.updatedAt;
      const live = Number(state.updatedAt) || 0;
      if (Number.isFinite(baseline) && live && live <= baseline) {
        return {
          ok: true,
          skipped: true,
          project: { latestVersion: Number(state.cloudVersion) || 0 },
          version: null,
          syncedUpdatedAt: baseline
        };
      }
    }

    // This callback and buildProjectZipBlob's structuredClone run in the same
    // synchronous turn. Collaboration uses it to bind save-notify.atSeq to the
    // exact room head represented by the uploaded bytes.
    const snapshotContext =
      typeof options.captureSnapshotContext === 'function'
        ? options.captureSnapshotContext(state)
        : undefined;
    // Hash the PORTABLE state — the exact shape that becomes state.json — not
    // the live one (live keeps cloudVersion + un-canonicalized media; hashing
    // it would produce false "no change" verdicts against saved bundles).
    // buildProjectZipBlob re-prepares internally; preparePortableState is
    // idempotent, so both operations see identical content.
    const prepared = preparePortableState(state);
    const meta = {
      projectId: state.projectId,
      name: state.projectName || 'Untitled',
      schemaVersion: state.schemaVersion || 1,
      updatedAt: state.updatedAt || Date.now(),
      // Optimistic-concurrency base: the server latest_version we last synced from
      // (0 = never synced → create-only). The server 409s if it advanced past this.
      baseVersion: Number(state.cloudVersion) || 0,
      // Migration 006: canonical content identity for this version. Label is an
      // optional human tag (publish flows set it).
      contentHash: await versionContentHashOf(prepared),
      ...(options.label ? { label: String(options.label) } : {})
    };
    const zipBlob = await buildProjectZipBlob(prepared);
    // Lazy-import auth-api so this module doesn't pull templates/* during the
    // pure save tests that golden-export-test.js uses.
    const { saveProject } = await import('../templates/auth-api.js');
    const result = await saveProject({ meta, zipBlob });
    // On a real save OR an idempotent dedupe, adopt the server's latest as our new
    // base so the next save doesn't self-conflict.
    await setCloudVersionAndPersist(state.projectId, Number(result?.project?.latestVersion));
    // Switcher thumbnail: fire-and-forget, self-throttled to one capture per
    // project per 30 minutes (the 30s auto-sync tick must not trigger renders).
    // Reached through a REGISTERED loader (CE-5): the builder shell registers
    // the lazy import at boot, so the thumbnail pipeline — which pulls the
    // whole exporter graph — is never wired into the guest surfaces, whose
    // static-graph gates forbid it. Unregistered = no thumbnail, which is the
    // correct guest behavior (guests never cloudSave at all).
    if (typeof window !== 'undefined' && _thumbnailLoader) {
      Promise.resolve()
        .then(() => _thumbnailLoader())
        .then(({ scheduleThumbnailCapture }) => {
          scheduleThumbnailCapture(state.projectId, {
            thumbUpdatedAt: Number(result?.project?.thumbUpdatedAt) || null
          });
        })
        .catch((err) => {
          reportError('SaveLoad thumbnail', err, {
            user: 'silent',
            detail: 'thumbnail module import failed after cloud save'
          });
        });
    }
    // What this save actually uploaded is now the server's content, so it is the
    // baseline a later skipIfUnchanged caller compares against.
    markCloudSynced(state.projectId, meta.updatedAt);
    const synced = { ...result, syncedUpdatedAt: Number(meta.updatedAt) };
    return snapshotContext === undefined ? synced : { ...synced, snapshotContext };
  };

  const result = _cloudSaveChain.then(run, run);
  // Keep the chain alive regardless of this save's outcome.
  _cloudSaveChain = result.then(
    () => {},
    () => {}
  );
  return result;
}

/**
 * Phase 6 UI (Tier 2): on app boot, if the user is authenticated and the
 * cloud copy of their current project is newer than the IDB autosave, fetch
 * the cloud version and load it via loadFromZip.
 *
 * Returns one of:
 *   - { restored: true, projectId, version }
 *                                        a newer cloud version was fetched + applied
 *   - { restored: false, pendingNewer: { projectId, version } }
 *                                        a newer cloud version exists but the store
 *                                        changed during metadata/download/load (a user
 *                                        edit or another load) — NOT applied, offered
 *                                        instead when still on the same project
 *   - { restored: false }                otherwise (no cloud version, IDB is fresher,
 *                                        not signed in, or fetch failed silently)
 *
 * Caller is shell.js, which passes the already-authenticated `user` (no internal
 * auth round-trip, so there is no localStorage-session-hint boot gate) and, in
 * tests, a mocked `api` seam. The IDB autosave check is upstream; this runs AFTER
 * restoreAutoSave completed so it can compare against the restored `state.updatedAt`.
 *
 * @param {{ user?: object, api?: object }} [opts]
 */
export async function tryRestoreFromCloudIfFresher({ user, api } = {}) {
  const state = store.getState();
  if (!state || !state.projectId) return { restored: false };
  const projectId = state.projectId;
  const epochAtCheck = store.getEpoch();
  if (!api) {
    try {
      api = await import('../templates/auth-api.js');
    } catch {
      // Expected in pure/offline test contexts where cloud restore is unavailable.
      return { restored: false };
    }
  }
  // The user is authenticated upstream by the shell; a non-real user (or none)
  // means local autosave stays the source of truth. No api calls in that case.
  if (!api.isRealUser(user)) return { restored: false };
  let projectMeta;
  try {
    projectMeta = await api.getProject(projectId);
  } catch (err) {
    // SB-1. This catch used to read EVERY failure as "no cloud copy yet" and
    // return silently — which is how a deleted project loaded from a stale tab
    // with no indication at all. The server cannot tell us why (it answers 404
    // for deleted, access-lost and never-existed alike, deliberately, so
    // membership cannot be probed), so the missing half is provenance: has this
    // project EVER been confirmed in cloud? See everConfirmedInCloud.
    const { lifecycle, signal } = classifyProjectSignal({
      operation: LIFECYCLE_OPERATION.READ,
      status: err?.status ?? null,
      everConfirmedInCloud: everConfirmedInCloud(state)
    });
    if (lifecycle === LIFECYCLE.UNAVAILABLE) return { restored: false, lifecycle, signal };
    // INDETERMINATE (offline, 5xx, 401) and LOCAL_ONLY (a project that has
    // simply never synced) both mean "keep local and carry on". Only a real
    // server error is worth reporting; absence and offline are expected flows.
    if (lifecycle === LIFECYCLE.INDETERMINATE && signal === 'read-error') {
      reportError('SaveLoad cloud restore', err, {
        user: 'silent',
        detail: `cloud project metadata check failed (${err?.status || 'unknown'})`
      });
    }
    return { restored: false };
  }
  const latestVersion = Number(projectMeta?.project?.latestVersion);
  if (!latestVersion) return { restored: false };
  // Freshness is version-based primarily: the cloud advanced past the version we
  // last synced from. Timestamps are used only for legacy projects that predate
  // cloudVersion tracking (null base) — there the wall clock is the best signal.
  const hasTrackedBase =
    typeof state.cloudVersion === 'number' && Number.isInteger(state.cloudVersion);
  const localCloudVersion = hasTrackedBase ? state.cloudVersion : null;
  let cloudIsNewer;
  if (hasTrackedBase) {
    cloudIsNewer = latestVersion > localCloudVersion;
  } else {
    const cloudUpdatedAt = Number(projectMeta?.project?.updatedAt || 0);
    const localUpdatedAt = Number(state.updatedAt || 0);
    cloudIsNewer = !!cloudUpdatedAt && cloudUpdatedAt > localUpdatedAt;
  }
  if (!cloudIsNewer) {
    // Keeping local. For a legacy/untracked project, adopt the cloud latest as our
    // base so the next save is latest+1 rather than a spurious baseVersion=0
    // create-conflict against the already-existing cloud row.
    if (!hasTrackedBase) await setCloudVersionAndPersist(projectId, latestVersion);
    return { restored: false };
  }
  if (store.getState()?.projectId !== projectId) return { restored: false };
  if (store.getEpoch() !== epochAtCheck) return pendingNewerResult(projectId, latestVersion);
  // The mutation/load epoch was captured before the metadata request and is
  // re-checked after every async boundary through the final apply. If it moves,
  // the user edited — or another load ran — so applying this cloud copy would
  // silently clobber their work. Detect and defer.
  try {
    const blob = await api.getProjectVersionBundle(projectId, latestVersion);
    if (store.getState()?.projectId !== projectId) return { restored: false };
    if (store.getEpoch() !== epochAtCheck) return pendingNewerResult(projectId, latestVersion);
    await loadFromZip(blob, { expectedEpoch: epochAtCheck, expectedProjectId: projectId });
    // The applied bundle carries no cloudVersion (stripped on export); set our base
    // to the version we just restored so the next save doesn't self-conflict.
    await setCloudVersionAndPersist(projectId, latestVersion);
    // Boot-path baseline: the in-memory project is now byte-for-byte a bundle the
    // server holds, so the first agent turn of this page lifetime can skip its
    // no-op pre-run save.
    markCloudSynced(projectId, store.getState()?.updatedAt);
    return { restored: true, projectId, version: latestVersion };
  } catch (err) {
    if (isLoadSupersededError(err)) {
      return err.reason === 'project-changed'
        ? { restored: false }
        : pendingNewerResult(projectId, latestVersion);
    }
    reportError('SaveLoad cloud restore', err, {
      user: 'toast',
      message: 'Could not restore the newer cloud version',
      detail: 'cloud version bundle restore failed',
      duration: 8000,
      id: 'cloud-restore-failed'
    });
    return { restored: false };
  }
}

/**
 * Explicitly load a specific cloud version bundle into the store, discarding the
 * current in-memory project. Used by the boot "Load cloud version" toast action
 * (see tryRestoreFromCloudIfFresher's `pendingNewer`): the background fresher-check
 * suppressed auto-apply because the user edited during the download, but the user
 * has now chosen to take the cloud copy anyway. Routes through loadFromZip →
 * applyLoadedProjectState so the autosave slot is synced.
 *
 * @param {string} projectId
 * @param {number} version
 * @param {{ api?: object, requireWritable?: boolean, expectedEpoch?: number,
 *   expectedProjectId?: string }} [opts] `api` is the test seam.
 * @returns {Promise<{ version: number }>}
 */
export async function restoreCloudVersion(
  projectId,
  version,
  { api, requireWritable = false, expectedEpoch, expectedProjectId } = {}
) {
  if (!api) api = await import('../templates/auth-api.js');
  const blob = await api.getProjectVersionBundle(projectId, version);
  await loadFromZip(blob, {
    requireWritable,
    expectedEpoch,
    expectedProjectId
  });
  // This path loads the cloud's latest (the pendingNewer/conflict "load cloud"
  // action), so the restored version IS the new sync base.
  await setCloudVersionAndPersist(projectId, Number(version));
  // The in-memory project now equals a bundle the server already holds, so a
  // skipIfUnchanged caller can trust this as a baseline without a round trip.
  markCloudSynced(projectId, store.getState()?.updatedAt);
  return { version };
}

function extensionFromMime(mime, filename) {
  // Try to get extension from original filename
  if (filename) {
    const match = filename.match(/\.([a-zA-Z0-9]+)$/);
    if (match) return match[1].toLowerCase();
  }
  // Fall back to mime type
  const map = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/avif': 'avif'
  };
  return map[mime] || 'bin';
}

/**
 * Single choke point for loading a fully-formed project state into the store.
 *
 * Runs store.init(state), then best-effort persists it to the autosave slot so a
 * refresh immediately after a load recovers the just-loaded project (not the
 * previously-open one, and not project A's stale slot after loading B). Persist
 * failure is non-fatal: the in-memory project is already live and a later
 * mutation re-triggers autosave — so it is reported silently, never thrown.
 *
 * Every path that loads a project from a source — file, zip, cloud bundle,
 * version restore, composer mockup, switcher Open — routes through here. Boot's
 * default-state init() deliberately does NOT (it must not overwrite the slot
 * before checkAutoSave() reads it).
 *
 * @param {object} state a migrated, validated project state
 * @param {{label?: string, expectedEpoch?: number, expectedProjectId?: string, requireWritable?: boolean}} [opts]
 */
export async function applyLoadedProjectState(
  state,
  { label = 'load', expectedEpoch, expectedProjectId, requireWritable = false } = {}
) {
  assertLoadStillCurrent({ expectedEpoch, expectedProjectId, requireWritable });
  await resolveLegacySectionProvenance(state);
  assertLoadStillCurrent({ expectedEpoch, expectedProjectId, requireWritable });
  store.init(state);
  // The intended-project pointer moves WITH the slot, not with the
  // `project:restored` event. That event is emitted by the cloud-shaped
  // transitions only, so Start Over and a file/ZIP load left the pointer naming
  // the project this tab had just left — and boot resolves the pointer, so a
  // refresh restored that abandoned project over the one on screen. Writing it
  // here is the same argument the slot write below already makes: this function
  // is the one place every source-load passes through.
  writeLastOpenedProject(store.getPrincipalScope(), store.getState()?.projectId);
  try {
    await store.saveToIDB();
  } catch (err) {
    reportError('SaveLoad slot sync', err, {
      user: 'silent',
      detail: `autosave slot write after ${label} failed`
    });
  }
}

/**
 * Load from a user-selected file. Detects format by extension/content.
 * Returns { state, assetCount } on success.
 */
export async function loadFromFile(file, opts = {}) {
  if (!file) throw new Error('No file provided');

  if (
    file.name.endsWith('.pb.zip') ||
    file.type === 'application/zip' ||
    file.name.endsWith('.zip')
  ) {
    return loadFromZip(file, opts);
  }
  return loadFromJSON(file, opts);
}

/**
 * The full state-shaping half of a load, shared by EVERY path that turns
 * serialized state back into a live-comparable one: migration (schema ladder,
 * defaults, id repair) + editable-id stamping (stampAllSections rewrites
 * section html/originalHtml). Split from the store side effects (asset
 * IDB writes/GC, applyLoadedProjectState) so read-only consumers —
 * readStateFromZip below — produce EXACTLY what a real load would have put in
 * the store. Without the stamping half, a fetched version bundle diffs against
 * the live (stamped) state with phantom data-pb-eid deltas, and re-hashing a
 * bundle could never reproduce the contentHash recorded at save time.
 */
export function canonicalizeLoadedState(state, { preserveDeviceOrigin = false } = {}) {
  if (!preserveDeviceOrigin) delete state.identityRepairLocalOrigin;
  migrateState(state);
  stampAllSections(state);
  return state;
}

async function loadFromJSON(file, opts = {}) {
  const text = await file.text();
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Invalid JSON file');
  }

  canonicalizeLoadedState(parsed);

  if (!validateState(parsed)) {
    throw new Error('File does not appear to be a valid Page Builder project');
  }
  // Acquiring the destination claim may legitimately clear a source-only
  // write blocker. Keep epoch/project guards here, then assert writability
  // after the destination has been reserved.
  assertLoadStillCurrent({ ...opts, requireWritable: false });
  await assertDestinationProjectClaimed(parsed);
  assertLoadStillCurrent(opts);
  stopBackgroundPrefetch({ invalidate: true });

  // Check for pb-asset:// references that won't resolve without assets
  const assetRefs = countAssetRefs(parsed);
  if (assetRefs > 0) {
    console.warn(
      `[SaveLoad] Loaded .pb.json with ${assetRefs} pb-asset:// reference(s) that may not resolve. Use .pb.zip for full save/load with assets.`
    );
  }

  // Clear blob URL cache + GC orphaned blobs from IDB
  store.assets.revokeAll();
  const refsInLoaded = collectReferencedAssetIds(parsed);
  await store.assets.gc(refsInLoaded);

  await applyLoadedProjectState(parsed, { ...opts, label: 'file load' });
  return { state: parsed, assetCount: 0, warningAssetRefs: assetRefs };
}

export async function loadFromZip(file, opts = {}) {
  // Writability can legitimately change when the destination lock is acquired
  // below (for example, leaving an unavailable project). Epoch/project guards
  // still apply before the download work.
  assertLoadStillCurrent({ ...opts, requireWritable: false });
  await loadJSZip();
  const zip = await JSZip.loadAsync(file);

  // Extract state.json
  const stateFile = zip.file('state.json');
  if (!stateFile) {
    throw new Error('ZIP does not contain state.json — not a valid .pb.zip file');
  }
  const stateText = await stateFile.async('string');
  let parsed;
  try {
    parsed = JSON.parse(stateText);
  } catch {
    throw new Error('Invalid state.json in ZIP');
  }

  canonicalizeLoadedState(parsed);

  if (!validateState(parsed)) {
    throw new Error('state.json does not appear to be a valid Page Builder project');
  }
  assertLoadStillCurrent({ ...opts, requireWritable: false });
  await assertDestinationProjectClaimed(parsed);
  assertLoadStillCurrent(opts);
  stopBackgroundPrefetch({ invalidate: true });

  // Clear old asset cache before loading new assets
  store.assets.revokeAll();

  // Attribution metadata for referenced assets (absent in old zips → {}).
  let attribution = {};
  const metaFile = zip.file('assets-meta.json');
  if (metaFile) {
    try {
      attribution = JSON.parse(await metaFile.async('string')) || {};
    } catch (err) {
      console.warn('[SaveLoad] Could not parse assets-meta.json — skipping attribution:', err);
      attribution = {};
    }
  }

  // Extract and store assets in IndexedDB
  let assetCount = 0;
  const assetsFolder = zip.folder('assets');
  if (assetsFolder) {
    const assetFiles = [];
    assetsFolder.forEach((relativePath, zipEntry) => {
      if (!zipEntry.dir) {
        assetFiles.push({ relativePath, zipEntry });
      }
    });

    for (const { relativePath, zipEntry } of assetFiles) {
      try {
        assertLoadStillCurrent(opts);
        const blob = await zipEntry.async('blob');
        assertLoadStillCurrent(opts);
        // Asset ID is the filename without extension
        const id = relativePath.replace(/\.[^.]+$/, '');
        const ext = relativePath.match(/\.([^.]+)$/)?.[1] || 'bin';
        const mime = mimeFromExtension(ext);
        const meta = attribution[id] || {};

        // Store directly into IndexedDB with the original ID (preserves refs).
        // notify:false — a ZIP restore must not echo every asset back into the
        // collab upload queue (the connect-time sweep covers genuinely missing
        // ones).
        await store.assets.storeWithId(
          id,
          blob,
          {
            filename: relativePath,
            mime,
            source: meta.source,
            author: meta.author,
            authorUrl: meta.authorUrl
          },
          { notify: false }
        );
        assetCount++;
      } catch (err) {
        console.error(`[SaveLoad] Failed to load asset ${relativePath}:`, err);
      }
    }
  }

  // GC any old blobs not referenced by the newly loaded state
  assertLoadStillCurrent(opts);
  const refsInLoaded = collectReferencedAssetIds(parsed);
  await store.assets.gc(refsInLoaded);

  await applyLoadedProjectState(parsed, { ...opts, label: 'zip load' });
  return { state: parsed, assetCount, warningAssetRefs: countMissingAssetRefs(parsed, assetCount) };
}

/** Recursive freeze so cached read-only states cannot be mutated by callers. */
function deepFreeze(value) {
  if (value == null || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const key of Object.keys(value)) deepFreeze(value[key]);
  return Object.freeze(value);
}

/**
 * READ a version bundle's state without touching the store: no asset IDB
 * writes, no GC, no applyLoadedProjectState — loadFromZip's destructive side
 * effects are exactly what a diff/preview reader must not have (the GC at
 * loadFromZip actively deletes old versions' assets, so "just load it" is not
 * an option). Runs the SAME canonicalizeLoadedState the live paths run, which
 * is what makes the result comparable to the live state and lets
 * versionContentHashOf reproduce the save-time contentHash. Frozen: the
 * version-state cache hands one object to every caller.
 *
 * @param {Blob} blob — a .pb.zip bundle
 * @returns {Promise<object>} frozen, canonicalized state
 */
export async function readStateFromZip(blob) {
  const { state } = await readBundleZip(blob);
  return state;
}

/**
 * Like readStateFromZip but also exposes the bundle's own assets as an
 * injectable asset source (the shape export/asset-resolver.js accepts), so a
 * page of an OLD version renders with the images it was saved with — the live
 * IDB provably no longer has them.
 *
 * @param {Blob} blob
 * @returns {Promise<{state: object, assetSource: {get(id: string): Promise<{blob: Blob, mime: string, filename: string}|null>}}>}
 */
export async function readProjectBundle(blob) {
  return await readBundleZip(blob);
}

async function readBundleZip(blob) {
  const Z = await loadJSZip();
  // ArrayBuffer, not the Blob itself: JSZip accepts Blobs only where its own
  // feature detection finds browser Blob support — Node (the test runner)
  // fails on them. The conversion is free in browsers.
  const bytes = typeof blob?.arrayBuffer === 'function' ? await blob.arrayBuffer() : blob;
  const zip = await Z.loadAsync(bytes);
  const stateFile = zip.file('state.json');
  if (!stateFile) {
    throw new Error('ZIP does not contain state.json — not a valid .pb.zip file');
  }
  let parsed;
  try {
    parsed = JSON.parse(await stateFile.async('string'));
  } catch {
    throw new Error('Invalid state.json in ZIP');
  }
  canonicalizeLoadedState(parsed);
  if (!validateState(parsed)) {
    throw new Error('state.json does not appear to be a valid Page Builder project');
  }

  // Lazy per-id reads; entries are only inflated when a renderer asks.
  const entries = new Map();
  const assetsFolder = zip.folder('assets');
  if (assetsFolder) {
    assetsFolder.forEach((relativePath, zipEntry) => {
      if (zipEntry.dir) return;
      const id = relativePath.replace(/\.[^.]+$/, '');
      entries.set(id, { relativePath, zipEntry });
    });
  }
  const assetSource = {
    async get(id) {
      const entry = entries.get(String(id));
      if (!entry) return null;
      const assetBlob = await entry.zipEntry.async('blob');
      const ext = entry.relativePath.match(/\.([^.]+)$/)?.[1] || 'bin';
      return { blob: assetBlob, mime: mimeFromExtension(ext), filename: entry.relativePath };
    }
  };

  return { state: deepFreeze(parsed), assetSource };
}

function mimeFromExtension(ext) {
  const map = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    avif: 'image/avif'
  };
  return map[ext.toLowerCase()] || 'application/octet-stream';
}

// Exported for the crm-bridge import round-trip test (B2), which must assert
// against the REAL validator, not a re-implementation.
export function validateState(obj) {
  // Structural validation with detailed checks
  if (obj == null || typeof obj !== 'object') return false;
  if (!Array.isArray(obj.pages) || obj.pages.length === 0) return false;
  if (typeof obj.activePage !== 'string') return false;
  if (obj.theme == null || typeof obj.theme !== 'object') return false;

  // Validate each page has required fields
  for (const page of obj.pages) {
    if (!page || typeof page !== 'object') return false;
    if (typeof page.id !== 'string' || !page.id) return false;
    if (typeof page.name !== 'string') return false;
    if (!Array.isArray(page.sections)) return false;
  }

  // Verify activePage references an existing page
  if (!obj.pages.some((p) => p.id === obj.activePage)) return false;

  return true;
}

function countAssetRefs(obj) {
  const json = JSON.stringify(obj);
  const matches = json.match(/pb-asset:\/\//g);
  return matches ? matches.length : 0;
}

/**
 * After ZIP import: count asset refs in state that weren't found in the ZIP.
 * assetCount is how many assets were successfully loaded from the ZIP.
 */
function countMissingAssetRefs(state, loadedAssetCount) {
  const referencedIds = collectReferencedAssetIds(state);
  // If we loaded fewer assets than are referenced, some are missing
  const missing = referencedIds.size - loadedAssetCount;
  return missing > 0 ? missing : 0;
}

/**
 * Check for auto-save recovery data in IndexedDB.
 * Returns { state, savedAt } or null.
 *
 * FN-5: a record whose envelope names a DIFFERENT account is refused, and
 * refusal means exactly that — the record is left on disk untouched. Clearing
 * it would delete the other account's only local copy on a shared machine,
 * which is the failure this check exists to prevent, inverted. A record with no
 * `ownerSub` predates the field; it stays recoverable rather than being
 * silently assigned to whoever signs in next.
 */
export async function checkAutoSave({ projectId = null } = {}) {
  try {
    // SB-3a: boot resolves an INTENDED project (deep link → last-opened pointer)
    // and probes that slot, instead of restoring whatever was written last.
    // Falls back to the legacy global slot while it is still being written.
    const saved = await store.loadFromIDB({ projectId });
    if (saved && saved.ownerSub !== undefined) {
      const principal = store.getPrincipalScope?.() ?? null;
      if (principal && saved.ownerSub !== principal) {
        recordSessionBoundaryMetric('autosave_recovery_refused', { reason: 'owner_mismatch' });
        return null;
      }
    }
    if (saved && saved.state && saved.savedAt) {
      migrateState(saved.state);
      if (validateState(saved.state)) {
        return { state: saved.state, savedAt: saved.savedAt };
      }
    }
    // If state exists but fails validation, clear it
    if (saved && saved.state && !validateState(saved.state)) {
      console.warn('[SaveLoad] Auto-save state failed validation — discarding');
      await store.clearIDB();
    }
  } catch {
    // IndexedDB may not be available
  }
  return null;
}

/**
 * Restore from auto-save (loads the saved state).
 */
export async function restoreAutoSave(savedState) {
  canonicalizeLoadedState(savedState, { preserveDeviceOrigin: true });
  await resolveLegacySectionProvenance(savedState);
  if (!validateState(savedState)) {
    throw new Error('Auto-save state failed validation after migration');
  }
  stopBackgroundPrefetch({ invalidate: true });
  store.init(savedState);
}

/**
 * Dismiss auto-save (clears IDB and starts fresh).
 */
export async function dismissAutoSave() {
  await store.clearIDB();
}
